import React from 'react';
import { Link } from 'react-router-dom';
import LocLink from '../components/LocLink';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { FaEtsy } from 'react-icons/fa';
import { trackProjectActClick } from '../lib/analytics';
import SEOHead from '../components/SEOHead';
import JsonLd, { breadcrumbSchema } from '../components/JsonLd';
import { getLocalizedImage, getLocalizedAlt } from '../utils/getLocalizedImage';
import USTariffNotice from '../components/USTariffNotice';
import { useUSTariffNotice } from '../hooks/useIsUSVisitor';
import {
  Brain,
  Zap,
  Shield,
  Heart,
  Pill,
  AlertTriangle,
  ExternalLink,
  ArrowRight,
} from 'lucide-react';

const IMAGES = {
  epipod: 'https://us.chat-img.sintra.ai/7edd9b7a-9f5a-467d-9e3a-be6d0559b75f/03732df8-b45c-492b-b164-0fc6530010bb/epipod_drop_water_-_Copie.png',
  nitropod: 'https://us.chat-img.sintra.ai/7edd9b7a-9f5a-467d-9e3a-be6d0559b75f/eae8501f-205c-478b-939b-742d8d6a8c6c/Nitropod_-_Copie.png',
  valtopod: 'https://us.chat-img.sintra.ai/7edd9b7a-9f5a-467d-9e3a-be6d0559b75f/7b7deaa9-ce03-4116-ac3f-fd39ff563482/Valtopod_drop.png',
  nayzipod: '/images/bgl1ur09_MGP_Nayzipod.png',
  tempswing: '/images/0zge02g6_tempswing-Photoroom.jpg',
  product: 'https://static.prod-images.emergentagent.com/jobs/eb3c3d69-05cb-4ec0-9ce4-18199acb6ff3/images/09f71b93832ad7723b8918fb4193cedc2e08bf8c7330d275cef2c544a6d1a60b.png',
};

const ProductsPage = () => {
  const { t, language } = useLanguage();
  const tariffNotice = useUSTariffNotice();

  const RIBBON_IMAGES = {
    epilepsy: '/images/l37azf4s_ChatGPT_Image_16_avr._2026_13_30_55.png',
    adrenal: '/images/pcbe2bml_ChatGPT_Image_16_avr._2026_13_33_33.png',
    allergy: '/images/k2bpgr2g_ChatGPT_Image_16_avr._2026_13_56_35.png',
    heart: '/images/0irdfe9w_ChatGPT_Image_16_avr._2026_14_24_08.png',
    overdose: '/images/cp9isp58_ChatGPT_Image_16_avr._2026_14_26_19.png',
    asthma: '/images/gn91kotm_ChatGPT_Image_16_avr._2026_14_30_36.png',
  };

  const productCategories = [
    {
      key: 'epilepsy',
      ribbon: RIBBON_IMAGES.epilepsy,
      lightColor: 'bg-red-50',
      textColor: 'text-[#E31B23]',
      image: IMAGES.nayzipod,
      products: ['ValtoPod', 'NayziPod', 'MidazoPod'],
      etsyUrl: 'https://www.etsy.com/shop/MyGuardPod?ref=seller-platform-mcnav&section_id=57806717',
    },
    {
      key: 'adrenal',
      ribbon: RIBBON_IMAGES.adrenal,
      lightColor: 'bg-neutral-100',
      textColor: 'text-[#1a1a1a]',
      image: '/images/r4njci01_iap_300x300.7786102811_57t46fw8.png',
      products: ['CortoPod ACT\u2122', 'CortoPod Plus ACT\u2122', 'CortoPod Mini ACT\u2122'],
      etsyUrl: 'https://www.etsy.com/shop/MyGuardPod?ref=seller-platform-mcnav&section_id=58650544',
    },
    {
      key: 'allergy',
      ribbon: RIBBON_IMAGES.allergy,
      lightColor: 'bg-red-50',
      textColor: 'text-[#E31B23]',
      image: '/images/qzyw63bg_608983763_10165123529866535_7433414729650333225_n.jpg',
      products: ['EpiPod', 'AuviPod', 'NefiPod'],
      etsyUrl: 'https://www.etsy.com/shop/MyGuardPod?ref=seller-platform-mcnav&section_id=57791610',
    },
    {
      key: 'heart',
      ribbon: RIBBON_IMAGES.heart,
      lightColor: 'bg-neutral-100',
      textColor: 'text-[#1a1a1a]',
      image: '/images/cmk9env8_il_fullxfull.6847611888_qupu.avif',
      products: ['NitroPod'],
      etsyUrl: 'https://www.etsy.com/shop/MyGuardPod?ref=seller-platform-mcnav&section_id=57806719',
    },
    {
      key: 'overdose',
      ribbon: RIBBON_IMAGES.overdose,
      lightColor: 'bg-red-50',
      textColor: 'text-[#E31B23]',
      image: '/images/vopo9i19_Naloxpod-Photoroom.png',
      products: ['NaloxPod'],
      etsyUrl: 'https://www.etsy.com/shop/MyGuardPod?ref=seller-platform-mcnav&section_id=53896058',
    },
    {
      key: 'asthma',
      ribbon: RIBBON_IMAGES.asthma,
      lightColor: 'bg-neutral-100',
      textColor: 'text-[#1a1a1a]',
      image: '/images/df9ntrsr_image.png',
      products: ['AsthmaPod\u2122'],
      etsyUrl: 'https://myguardpod.etsy.com/listing/4450337764',
    },
  ];

  return (
    <main className="pt-20 sm:pt-24">
      <SEOHead
        routeKey="products"
        language={language}
        title={language === 'fr' ? 'Produits — Étuis MyGuardPod' : 'Products — MyGuardPod Cases'}
        description={language === 'fr'
          ? 'Découvrez la gamme complète d’étuis protecteurs MyGuardPod par catégorie médicale, incluant l’initiative Project ACT™ pour l’hydrocortisone d’urgence.'
          : 'Discover the full range of MyGuardPod protective cases by medical category, including the Project ACT™ initiative for emergency hydrocortisone.'}
      />
      <JsonLd data={breadcrumbSchema([{ name: language === 'fr' ? 'Produits' : 'Products', routeKey: 'products' }], language)} />
      {/* Banner */}
      <section className="overflow-hidden banner-fade">
        <img
          src={getLocalizedImage('productsHero', language)}
          alt={getLocalizedAlt('productsHero', language)}
          className="w-full h-auto object-cover"
        />
      </section>

      {/* Product Grid */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {tariffNotice.show && <USTariffNotice className="mb-12" onDismiss={tariffNotice.dismiss} />}
          <div className="text-center mb-12">
            <h1 className="font-outfit font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1a1a1a] mb-4">
              {t('products.title')}
            </h1>
            <p className="font-manrope text-lg text-neutral-600 max-w-2xl mx-auto">
              {t('products.subtitle')}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Featured initiative — Project ACT */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="group"
            >
              <LocLink
                to="projectAct"
                onClick={() => trackProjectActClick('products_featured')}
                className="block bg-neutral-50 rounded-3xl overflow-hidden hover:shadow-xl transition-all duration-300 ring-1 ring-[#E31B23]/30 hover:ring-[#E31B23]/60 relative"
                data-testid="featured-project-act"
              >
                {/* Featured badge */}
                <div className="absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 bg-[#1a1a1a] text-white px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider font-manrope">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#E31B23] animate-pulse" />
                  {language === 'fr' ? 'Initiative en vedette' : 'Featured Initiative'}
                </div>

                {/* Image */}
                <div className="aspect-square overflow-hidden bg-white">
                  <img
                    src="/images/sv0z24k6_lineup_project_ACT.jpg"
                    alt="Project ACT - Adrenal Crisis Transport"
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                  />
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="mb-3">
                    <h3 className="font-outfit font-black text-xl text-[#1a1a1a] leading-tight">
                      Project ACT{'\u2122'}
                    </h3>
                    <p className="font-manrope text-sm text-[#E31B23] font-semibold mt-0.5">
                      {language === 'fr' ? 'Transport en crise surr\u00e9nalienne' : 'Adrenal Crisis Transport'}
                    </p>
                  </div>

                  <p className="font-manrope text-sm text-neutral-600 mb-5 leading-relaxed">
                    {language === 'fr'
                      ? 'Une initiative d\u00e9di\u00e9e \u00e0 am\u00e9liorer la fa\u00e7on dont les kits d\u2019hydrocortisone injectable d\u2019urgence sont transport\u00e9s, prot\u00e9g\u00e9s, identifi\u00e9s et accessibles dans la vie r\u00e9elle.'
                      : 'A dedicated initiative focused on improving how emergency hydrocortisone injection kits are carried, protected, identified, and accessed in real life.'}
                  </p>

                  {/* Product tags */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {['CortoPod Mini ACT\u2122', 'CortoPod ACT\u2122', 'CortoPod Plus ACT\u2122', language === 'fr' ? 'Kits d\u2019hydrocortisone d\u2019urgence' : 'Emergency hydrocortisone kits'].map((tag) => (
                      <span
                        key={tag}
                        className="bg-white border border-neutral-200 text-[#1a1a1a] px-3 py-1 rounded-full text-xs font-medium"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <span className="inline-flex items-center gap-2 text-[#E31B23] group-hover:text-[#c8171e] font-bold transition-colors">
                    <span>{language === 'fr' ? 'D\u00e9couvrir Project ACT' : 'Learn about Project ACT'}</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </div>
              </LocLink>
            </motion.div>

            {productCategories.map((category, index) => (
              <motion.div
                key={category.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group"
              >
                <div className="bg-neutral-50 rounded-3xl overflow-hidden hover:shadow-xl transition-all duration-300">
                  {/* Image */}
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={category.image}
                      alt={t(`products.categories.${category.key}.title`)}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <img src={category.ribbon} alt="" className="w-10 h-10 object-contain" />
                      <h3 className="font-outfit font-bold text-xl text-[#1a1a1a]">
                        {t(`products.categories.${category.key}.title`)}
                      </h3>
                    </div>

                    <p className="font-manrope text-sm text-neutral-500 mb-4 -mt-2">
                      {t(`products.categories.${category.key}.products`)}
                    </p>

                    {/* Products List */}
                    <div className="flex flex-wrap gap-2 mb-6">
                      {category.products.map((product) => (
                        <span
                          key={product}
                          className={`${category.lightColor} ${category.textColor} px-3 py-1 rounded-full text-sm font-medium`}
                        >
                          {product}
                        </span>
                      ))}
                    </div>

                    <a
                      href={category.etsyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-[#E31B23] hover:text-[#c8171e] font-bold transition-colors"
                      data-testid={`shop-${category.key}`}
                    >
                      <span>{language === 'fr' ? 'Voir sur Etsy' : 'View on Etsy'}</span>
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Custom Orders CTA */}
      <section className="py-16 sm:py-24 bg-[#1a1a1a]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-outfit font-black text-3xl sm:text-4xl text-white mb-4">
              {language === 'fr' ? 'Besoin d\u2019un \u00e9tui personnalis\u00e9?' : 'Need a Custom Case?'}
            </h2>
            <p className="font-manrope text-lg text-neutral-400 mb-8">
              {language === 'fr'
                ? 'Nous pouvons concevoir un \u00e9tui sur mesure pour votre m\u00e9dicament sp\u00e9cifique. Contactez-nous pour discuter de vos besoins.'
                : 'We can design a custom case for your specific medication. Contact us to discuss your needs.'}
            </p>
            <a
              href="/contact"
              className="inline-flex items-center space-x-2 bg-[#E31B23] hover:bg-[#c8171e] text-white px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 active:scale-95"
              data-testid="custom-order-cta"
            >
              <span>{language === 'fr' ? 'Nous contacter' : 'Contact Us'}</span>
            </a>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default ProductsPage;
